import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, Users, ChevronDown, ChevronRight, DollarSign, AlertCircle,
    Award, Percent, Download, UserCog,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const NAVY = "#0A2540";
const GOLD = "#C9A84C";
const COBALT = "#1E6FD9";

const fmtCents = (c) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((c || 0) / 100);

/** Flatten the recursive tree into an array of rows for display. */
function flattenTree(reports, expanded) {
    const out = [];
    function walk(nodes, level) {
        for (const n of nodes) {
            const hasChildren = Array.isArray(n.reports) && n.reports.length > 0;
            out.push({ node: n, level, hasChildren });
            if (hasChildren && expanded.has(n.id)) {
                walk(n.reports, level + 1);
            }
        }
    }
    walk(reports || [], 0);
    return out;
}

function initialExpanded(reports) {
    // Auto-expand first level.
    const s = new Set();
    for (const r of reports || []) s.add(r.id);
    return s;
}

export default function MyTeamTree() {
    const [tree, setTree] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(new Set());

    const load = useCallback(() => {
        setLoading(true);
        adminApi.myTeam("full")
            .then((t) => {
                setTree(t);
                setExpanded(initialExpanded(t?.reports));
            })
            .catch((err) => toast.error(err?.response?.data?.detail || "Couldn't load team tree."))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const stats = useMemo(() => {
        if (!tree?.reports) return { direct: 0, closed: 0, premium: 0, alerts: 0 };
        let direct = tree.reports.length;
        let closed = 0, premium = 0, alerts = 0;
        function walk(nodes) {
            for (const n of nodes) {
                const m = n.metrics || {};
                closed += m.policies_closed_ytd || 0;
                premium += m.total_annual_premium_cents || 0;
                alerts += m.open_alerts_count || 0;
                if (n.reports) walk(n.reports);
            }
        }
        walk(tree.reports);
        return { direct, closed, premium, alerts };
    }, [tree]);

    const rows = useMemo(() => flattenTree(tree?.reports, expanded), [tree, expanded]);

    const toggle = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const exportCSV = () => {
        if (!tree?.reports?.length) return;
        const csvRows = [["Depth", "Name", "Role", "Email", "Policies YTD", "Annual Premium", "Placement %", "Open Alerts"]];
        function walk(nodes, depth) {
            for (const n of nodes) {
                const m = n.metrics || {};
                csvRows.push([
                    depth,
                    `${n.first_name || ""} ${n.last_name || ""}`.trim(),
                    n.role || "",
                    n.email || "",
                    m.policies_closed_ytd || 0,
                    ((m.total_annual_premium_cents || 0) / 100).toFixed(2),
                    m.placement_rate_pct == null ? "" : m.placement_rate_pct,
                    m.open_alerts_count || 0,
                ]);
                if (n.reports) walk(n.reports, depth + 1);
            }
        }
        walk(tree.reports, 1);
        const csv = csvRows.map((r) => r.map((c) => {
            const s = String(c ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        }).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `my-team-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center">
                <Loader2 className="animate-spin text-mm-navy" size={28} />
            </div>
        );
    }

    const empty = !tree?.reports?.length;

    return (
        <div className="space-y-5" data-testid="admin-my-team">
            <header className="flex items-end justify-between gap-3">
                <div>
                    <div className="mm-eyebrow flex items-center gap-1.5"><UserCog size={11} /> Hierarchy</div>
                    <h1 className="mm-display text-3xl text-mm-navy">My Team</h1>
                    <p className="mt-1 text-sm text-mm-muted">Your direct reports and their downlines, YTD.</p>
                </div>
                <button
                    onClick={exportCSV}
                    disabled={empty}
                    className="mm-btn-outline !py-1.5 text-xs disabled:opacity-40"
                    data-testid="my-team-export"
                >
                    <Download size={12} /> Export CSV
                </button>
            </header>

            {/* KPI cards */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="my-team-kpis">
                <KPI icon={<Users size={13} />} label="Direct reports" value={stats.direct.toString()} testid="kpi-direct" />
                <KPI icon={<Award size={13} />} label="Policies closed YTD" value={stats.closed.toString()} testid="kpi-closed" />
                <KPI icon={<DollarSign size={13} />} label="Total annual premium YTD" value={fmtCents(stats.premium)} testid="kpi-premium" />
                <KPI icon={<AlertCircle size={13} className="text-rose-700" />} label="Open alerts (team)" value={stats.alerts.toString()} testid="kpi-alerts" />
            </div>

            {/* Tree (flattened list) */}
            <section className="mm-card p-4" data-testid="my-team-tree">
                {empty ? (
                    <div className="py-8 text-center text-sm text-mm-muted">
                        No direct reports yet. Assign uplines on the <a className="text-mm-navy hover:underline" href="/admin/users">Users</a> page to build your team tree.
                    </div>
                ) : (
                    <ul className="space-y-0.5">
                        {rows.map((row) => (
                            <TreeRow
                                key={row.node.id}
                                node={row.node}
                                level={row.level}
                                hasChildren={row.hasChildren}
                                isOpen={expanded.has(row.node.id)}
                                onToggle={() => toggle(row.node.id)}
                            />
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

function TreeRow({ node, level, hasChildren, isOpen, onToggle }) {
    const m = node.metrics || {};
    const testId = `team-node-${node.id}`;
    const paddingLeft = 8 + level * 20;

    return (
        <li
            className="rounded-md"
            data-testid={testId}
            style={{ paddingLeft }}
        >
            <div className="flex items-center gap-2 rounded-md px-2 py-2 hover:bg-mm-bg/40">
                <button
                    type="button"
                    onClick={onToggle}
                    disabled={!hasChildren}
                    className="flex h-5 w-5 shrink-0 items-center justify-center text-mm-muted disabled:opacity-30"
                    aria-label={isOpen ? "Collapse" : "Expand"}
                    data-testid={`team-node-toggle-${node.id}`}
                >
                    {hasChildren
                        ? (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />)
                        : <span className="h-1 w-1 rounded-full bg-mm-line" />}
                </button>
                <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                    style={{ background: `linear-gradient(135deg, ${NAVY}, ${COBALT})` }}
                >
                    {((node.first_name || "").slice(0, 1) + (node.last_name || "").slice(0, 1)).toUpperCase() || "MM"}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-mm-navy">
                        {node.first_name} {node.last_name}
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-mm-muted">{(node.role || "").replace("_", " ")}</span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-3 text-[11px] text-mm-muted">
                        <span data-testid={`team-node-closed-${node.id}`}>
                            <Award size={9} className="mr-0.5 inline -mt-0.5" /> {m.policies_closed_ytd || 0} closed
                        </span>
                        <span data-testid={`team-node-premium-${node.id}`}>
                            <DollarSign size={9} className="mr-0.5 inline -mt-0.5" /> {fmtCents(m.total_annual_premium_cents)}
                        </span>
                        {m.placement_rate_pct != null && (
                            <span>
                                <Percent size={9} className="mr-0.5 inline -mt-0.5" /> {Math.round(m.placement_rate_pct)}% placement
                            </span>
                        )}
                        {m.open_alerts_count > 0 && (
                            <span className="text-rose-700">
                                <AlertCircle size={9} className="mr-0.5 inline -mt-0.5" /> {m.open_alerts_count} alert{m.open_alerts_count === 1 ? "" : "s"}
                            </span>
                        )}
                    </div>
                </div>
                <a
                    href={`/admin/team/${node.id}`}
                    className="hidden text-[11px] font-medium text-mm-navy hover:underline sm:inline"
                    data-testid={`team-node-open-${node.id}`}
                >
                    Open →
                </a>
            </div>
        </li>
    );
}

function KPI({ icon, label, value, accent, testid }) {
    return (
        <div className="mm-card p-4" data-testid={testid}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-mm-muted">
                {icon}
                {label}
            </div>
            <div className="mm-display mt-1 text-2xl tabular-nums" style={{ color: accent || NAVY }}>{value}</div>
        </div>
    );
}
